<template>
  <div class="roles-main">
    <el-card shadow="never" class="filter-card">
      <div class="search">
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="Start Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="End Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-button
          icon="el-icon-search"
          size="small"
          type="success"
          @click="getRatingStatistics()"
        >
          Search
        </el-button>
        <el-tooltip content="Download" placement="top">
          <el-button icon="el-icon-download"
                     size="small"
                     type="success"
                     title="Download"
                     @click="download()"
          />
        </el-tooltip>
      </div>
    </el-card>
    <el-card shadow="never" class="section-card" v-if="list.records.length > 0">
      <div class="card-head">
        <span class="card-title">Rating Overview</span>
        <span class="card-badge">Total Count {{ totalCount }}</span>
      </div>
      <div class="rating-overview">
        <star-rating v-model:rating="rating" :increment="0.1" :read-only="true" :rounded-corners="true" :padding="10" star-size="30" inline="true" :show-rating="false" />
        <div class="rating-score">
          <span class="rating-num">{{ rating }}</span>
          <span class="rating-max">/5</span>
        </div>
      </div>
    </el-card>
    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Rating Distribution</span>
      </div>
      <el-table
        class="beauty-table"
        :data="list.records"
        ref="table"
        row-key="id"
        size="small"
        lazy
        border
        highlight-current-row
        v-loading="loading"
        empty-text="No data"
        :cell-class-name="cellGrayWhite"
      >
        <el-table-column prop="rating" label="Rating" align="left" min-width="160">
          <template #default="scope">
            <star-rating v-model:rating="scope.row.rating" :read-only="true" :rounded-corners="true" :padding="5" star-size="20" inline="true" :show-rating="false" style="margin: 5px 0px 0px;" />
          </template>
        </el-table-column>
        <el-table-column prop="count" label="Count" header-align="right" align="right" min-width="120">
          <template #default="scope">
            <span class="num-cell">{{ scope.row.count }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="percentage" label="Percentage" header-align="right" align="right" min-width="200">
          <template #default="scope">
            <div class="pct-cell">
              <el-progress :percentage="pct(scope.row.percentage)" :stroke-width="6" :show-text="false" class="pct-bar" />
              <span class="num-cell pct-num">{{ (Number(scope.row.percentage) || 0) }} %</span>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Rating By User</span>
      </div>
      <el-table
        class="beauty-table"
        :data="list.userRatingRecords"
        ref="table"
        row-key="identifier"
        size="small"
        lazy
        border
        highlight-current-row
        v-loading="loading"
        empty-text="No data"
        :tree-props="{children: 'children', hasChildren: 'hasChildren'}"
        :cell-class-name="cellWrapUpGrayWhite"
      >
        <el-table-column prop="loginName" label="Login Name" align="left" show-overflow-tooltip min-width="180" />
        <el-table-column prop="rating" label="Rating" align="left" min-width="160">
          <template #default="scope">
            <star-rating v-model:rating="scope.row.rating" :rounded-corners="true" :read-only="true" :increment="0.1" :padding="5" star-size="20" inline="true" style="margin: 5px 0px 0px;" />
          </template>
        </el-table-column>
        <el-table-column prop="count" label="Rating Count" header-align="right" align="right" min-width="130">
          <template #default="scope">
            <span class="num-cell">{{ scope.row.count }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="percentage" label="Count Percentage" header-align="right" align="right" min-width="200">
          <template #default="scope">
            <div class="pct-cell">
              <el-progress :percentage="pct(scope.row.percentage)" :stroke-width="6" :show-text="false" class="pct-bar" />
              <span class="num-cell pct-num">{{ (Number(scope.row.percentage) || 0) }} %</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="loginName" label="Comments" width="150px" align="center">
          <template #default="scope">
            <div
              v-if="scope.row.loginName"
              style="text-decoration: underline;cursor: pointer;"
              @click="showComments(scope.row.loginName)"
            >
              View Comments
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <el-drawer
      v-model="drawer"
      title="Comments"
      :with-header="false"
      :destroy-on-close="true"
      size="50%"
      :open-delay="300"
    >
      <roomComments :loginName="name" :startDate="startDate" :endDate="endDate" />
    </el-drawer>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from '@vue/runtime-core'
import { ElMessage } from 'element-plus'
import StarRating from 'vue-star-rating'
import roomComments from './comment'
import { getRatingStats, getUserRatingStats } from '../../../api/report'
import { convertDate, exportExl } from '@/utils/util'
import { cellGrayWhite, cellWrapUpGrayWhite } from '@/utils/style'

const list = reactive({
  records: [],
  userRatingRecords: []
})

const loading = ref(false)
const drawer = ref(false)
const name = ref('')

const startDate = ref(convertDate(new Date()))
const endDate = ref(convertDate(new Date()))

const totalCount = ref(0)
const rating = ref(0)

function pct(v) {
  const n = Number(v)
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 100) : 0
}

async function download() {
  try {
    const json = [];
    list.userRatingRecords.forEach((item, index) => {
      json.push({ login_name: item.loginName, rating: item.rating, rating_count: item.count })
    });
    json.push({ count: totalCount.value });
    const fileName = `${startDate.value}-${endDate.value}.xls`;
    exportExl(json, fileName);
  } catch (e) {
    ElMessage.error('Failed to load data')
  }
}

function compare(a, b) {
  if (a.rating < b.rating) {
    return 1;
  }
  if (a.rating > b.rating) {
    return -1;
  }
  return 0;
}

async function getRatingStatistics() {
  loading.value = true
  try {
    const { data: res } = await getRatingStats(startDate.value, endDate.value)
    res.sort(compare);
    list.records = res

    if (res.length > 0) {
      totalCount.value = res
        .map(stats => stats.count)
        .reduce((acc, count) => acc + count)

      rating.value = (res.map(stats => stats.rating * stats.count)
        .reduce((acc, count) => acc + count) / totalCount.value)
        .toFixed(1)
    }

    const { data: user } = await getUserRatingStats(startDate.value, endDate.value)
    list.userRatingRecords = user
    list.userRatingRecords.forEach(
      userRating => {
        let ratingSum = 0;
        if (Array.isArray(userRating.children) && userRating.children.length > 0) {
          userRating.children.sort(compare)
          userRating.children.forEach(
            userRatingChild => {
              ratingSum += (userRatingChild.rating * userRatingChild.count)
            }
          )
        }
        userRating.rating = (ratingSum / userRating.count).toFixed(1)
      }
    )
    list.userRatingRecords.sort(compare);
  } catch (e) {
    ElMessage.error('Failed to load data')
  } finally {
    loading.value = false
  }
}

function showComments(loginName) {
  drawer.value = true
  name.value = loginName
}

onMounted(async () => {
  await getRatingStatistics()
})
</script>

<style scoped>
.filter-card { margin-bottom:16px; border-radius:8px; }
.filter-card :deep(.el-card__body){ padding:16px 18px; }
.search { display:flex; align-items:center; flex-wrap:wrap; row-gap:10px; }
.search > *:not(h6) + * { margin-left:20px; }
.section-card { border-radius:10px; margin-bottom:16px; }
.section-card :deep(.el-card__body){ padding:16px 18px; }
.card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.card-title { font-size:15px; font-weight:600; color:var(--el-text-color-primary,#303133); }
.card-badge { font-size:12px; color:var(--el-text-color-secondary,#909399); font-variant-numeric:tabular-nums; }
.beauty-table :deep(thead th){ background:#f5f7fa; font-weight:600; color:var(--el-text-color-primary,#303133); }
.num-cell { font-variant-numeric:tabular-nums; }
.pct-cell { display:flex; align-items:center; gap:8px; justify-content:flex-end; }
.pct-bar { flex:1; min-width:80px; }
.pct-num { white-space:nowrap; color:var(--el-text-color-secondary,#909399); }
.rating-overview { display:flex; align-items:center; gap:24px; }
.rating-score { display:flex; align-items:baseline; gap:4px; }
.rating-num { font-size:28px; font-weight:600; }
.rating-max { font-size:14px; color:#909399; }
</style>
